// Barras de las últimas semanas cerradas de una regla, tienda o producto, más la semana en curso y
// la anterior mientras no se cierra.
import { useState } from 'react';
import { Button, Skeleton } from '@/kit';
import { useLivePayout, usePayoutSeries } from '../../api/commissionQueries';
import { contributorsOf } from '../../lib/paidRows';
import {
    addDays,
    localToday,
    WEEK_WINDOWS,
    weekStart,
    type WeeksWindow,
} from '../../lib/payoutPeriods';
import { SECTION_TITLE } from '../fieldStyles';
import { WeeklyBars } from './WeeklyBars';

interface SeriesSectionProps {
    by: 'RULE' | 'STORE' | 'SKU';
    id: string;
    /** Lo de la semana en curso, que la serie de cortes cerrados no trae; `null` sin corte vivo. */
    currentCents: number | null;
    /** El corte en curso; sin él, el lunes de esta semana. */
    currentPeriodId: string | undefined;
    /** El corte vivo todavía carga. */
    currentLoading: boolean;
    title: string;
}

export function SeriesSection({
    by,
    id,
    currentCents,
    currentPeriodId,
    currentLoading,
    title,
}: SeriesSectionProps) {
    const [weeks, setWeeks] = useState<WeeksWindow>(12);
    const series = usePayoutSeries(by, id, weeks);
    const current = currentPeriodId ?? weekStart(localToday());
    const points = series.data?.points ?? [];
    // Sin corte vivo no se pinta la semana en $0; si ya está entre los cerrados, no se repite.
    const showCurrent = currentCents !== null && !points.some((p) => p.periodId === current);
    // La serie solo trae cortes cerrados: el anterior, mientras no se cierra, va en vivo.
    const previousId = addDays(current, -7);
    const previousOpen =
        series.data !== undefined && !points.some((p) => p.periodId === previousId);
    const previous = useLivePayout(previousId, previousOpen);
    const previousCents =
        previousOpen && previous.data
            ? contributorsOf(previous.data.sellers, by, id).reduce((sum, c) => sum + c.paidCents, 0)
            : null;
    const bars = [
        ...points.map((point) => ({
            periodId: point.periodId,
            cents: point.paidCents,
            current: false,
            clawback: false,
        })),
        ...(previousCents === null
            ? []
            : [{ periodId: previousId, cents: previousCents, current: false, clawback: false }]),
        ...(showCurrent
            ? [{ periodId: current, cents: currentCents, current: true, clawback: false }]
            : []),
    ];
    return (
        <section className="border-b border-border px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className={SECTION_TITLE}>
                    {title} · últimas {weeks} semanas
                </h3>
                <div className="flex gap-1" role="group" aria-label="Semanas de historial">
                    {WEEK_WINDOWS.map((size) => (
                        <Button
                            key={size}
                            size="sm"
                            variant={size === weeks ? 'secondary' : 'ghost'}
                            aria-pressed={size === weeks}
                            onClick={() => {
                                setWeeks(size);
                            }}
                        >
                            {size}s
                        </Button>
                    ))}
                </div>
            </div>
            {series.isLoading || currentLoading || (previousOpen && previous.isLoading) ? (
                <Skeleton className="h-24" />
            ) : (
                <WeeklyBars bars={bars} selected={showCurrent ? current : null} label={title} />
            )}
            {series.isError && (
                <p className="mt-1 text-[10.5px] text-destructive">
                    No se pudo leer el historial de cortes.
                </p>
            )}
            {previousOpen && previous.isError && (
                <p className="mt-1 text-[10.5px] text-destructive">
                    No se pudo leer la semana anterior, que sigue sin cerrar.
                </p>
            )}
            {!currentLoading && currentCents === null && (
                <p className="mt-1 text-[10.5px] text-destructive">
                    No se pudo leer la semana en curso.
                </p>
            )}
        </section>
    );
}
